import { isTutorial } from "../../tutorial/tutorialStorage";
import { normalizeQuestionCanvasData } from "../utils/questionFrames";
import { useState } from "react";
import {
  CanvasElement,
  SubmissionResult,
  Tab,
  PaletteTab,
  VisualStyle,
} from "../../shared/types";
import {
  loadInitialCanvasData,
  loadInitialUIData,
  clearCanvasStorage,
} from "../utils/localStorage";
import type { QuestionView } from "../utils/localStorage";

import { readQuestionLink, readCategoryLink, invalidQuestionLink, questionTypes } from "../../informationTabs/questionTab/utils/questionLinks";

// Layout constants
const DEFAULT_INFO_PANEL_WIDTH = 500;

export function useMemoryModelEditorState(sandbox: boolean) {
    // Load initial data
  const tutorial = isTutorial();
  const [linkedQuestion, setLinkedQuestion] = useState(() =>
    tutorial ? null : readQuestionLink(window.location.search)
  );

  const [categoryLink] = useState(() => tutorial ? null : readCategoryLink(window.location.search));
  const [invalidLink] = useState(() => tutorial ? false : invalidQuestionLink(window.location.search));
  const initialCanvasData = tutorial
    ? normalizeQuestionCanvasData(loadInitialCanvasData())
    : linkedQuestion === null && categoryLink === null && !invalidLink
      ? loadInitialCanvasData()
      : { elements: [], ids: [], classes: [] };

  // Copy before applying overrides to avoid changing shared defaults.
  const initialUIData = { ...loadInitialUIData() };

  if (tutorial) {
    initialUIData.questionIndex = 1;
    initialUIData.questionType = "practice";
    initialUIData.questionView = "question";
    initialUIData.activeTab = "question";
    initialUIData.isInfoPanelOpen = true;
    initialUIData.sandboxMode = true;
  } else if (linkedQuestion !== null) {
    initialUIData.questionIndex = linkedQuestion.id;
    initialUIData.questionType = linkedQuestion.type;
    initialUIData.questionView = "question";
    initialUIData.activeTab = "question";
    initialUIData.isInfoPanelOpen = true;
    initialUIData.sandboxMode = false;
    initialUIData.submissionResults = null;
  }

  if (!tutorial && (categoryLink || invalidLink)) {
    const types = questionTypes.filter(type => new URLSearchParams(window.location.search).has(type));
    initialUIData.questionIndex = null;
    initialUIData.questionType = categoryLink ?? (types.length === 1 ? types[0] : null);
    initialUIData.questionView = initialUIData.questionType ? "list" : "root";
    initialUIData.activeTab = "question";
    initialUIData.isInfoPanelOpen = true;
    initialUIData.submissionResults = null;
  }

  // Canvas state
  const [canvasResetKey, setCanvasResetKey] = useState(0);
  const [elements, setElements] = useState<CanvasElement[]>(
    initialCanvasData.elements
  );
  const [elementIds, setElementIds] = useState<number[]>(initialCanvasData.ids);
  const [elementClasses, setElementClasses] = useState<string[]>(
    initialCanvasData.classes
  );
  const [jsonOutput, setJsonOutput] = useState<string>("");

  // UI state
  const [activeInfoTab, setActiveInfoTab] = useState<Tab>(
    initialUIData.activeTab
  );
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<
    number | null
  >(initialUIData.questionIndex);
  const [selectedQuestionType, setSelectedQuestionType] = useState<
    "test" | "practice" | "prep" | "experiment" | null
  >(initialUIData.questionType);
  const [submissionResults, setSubmissionResults] = useState<SubmissionResult | null>(
    initialUIData.submissionResults
  );
  const [isSandboxMode, setIsSandboxMode] = useState<boolean>(() =>
    typeof initialUIData.sandboxMode === "boolean"
      ? initialUIData.sandboxMode
      : sandbox
  );
  const [visualStyle, setVisualStyle] = useState<VisualStyle>(
    initialUIData.visualStyle ?? "memoryviz"
  );
  const [pythonTutorReferenceArrows, setPythonTutorReferenceArrows] =
    useState<boolean>(initialUIData.pythonTutorReferenceArrows ?? false);
  const [pythonTutorStandalonePrimitives, setPythonTutorStandalonePrimitives] =
    useState<boolean>(initialUIData.pythonTutorStandalonePrimitives ?? false);
  const [showLinkedListView, setShowLinkedListView] = useState<boolean>(
    initialUIData.showLinkedListView ?? false
  );
  const [structurePanelCollapsed, setStructurePanelCollapsed] = useState<boolean>(
    initialUIData.structurePanelCollapsed ?? false
  );
  const [structurePanelHeight, setStructurePanelHeight] = useState<number>(
    initialUIData.structurePanelHeight ?? 180
  );
  const [questionView, setQuestionView] = useState<QuestionView>(
    initialUIData.questionView ?? "root"
  );

  // Scroll positions for info panel tabs (persists across panel close/open)
  const [tabScrollPositions, setTabScrollPositions] = useState<Record<Tab, number>>({
    question: 0,
  });

  // Panel state
  const [activePaletteTab, setActivePaletteTab] = useState<PaletteTab>("all");
  const [isPaletteOpen, setIsPaletteOpen] = useState(true);
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(
    isTutorial() ? true : typeof initialUIData.isInfoPanelOpen === "boolean" ? initialUIData.isInfoPanelOpen : true
  );
  const [infoPanelWidth, setInfoPanelWidth] = useState<number>(
    DEFAULT_INFO_PANEL_WIDTH
  );
  const [isResizingInfoPanel, setIsResizingInfoPanel] =
    useState<boolean>(false);

  // Modal state
  const [showClearCanvasModal, setShowClearCanvasModal] =
    useState<boolean>(false);
  const [showModeToggleModal, setShowModeToggleModal] =
    useState<boolean>(false);

  return {
    linkedQuestion,
    setLinkedQuestion,
    // Canvas state
    canvasResetKey,
    setCanvasResetKey,
    elements,
    setElements,
    elementIds,
    setElementIds,
    elementClasses,
    setElementClasses,
    jsonOutput,
    setJsonOutput,

    // UI state
    activeInfoTab,
    setActiveInfoTab,
    selectedQuestionIndex,
    setSelectedQuestionIndex,
    selectedQuestionType,
    setSelectedQuestionType,
    submissionResults,
    setSubmissionResults,
    isSandboxMode,
    setIsSandboxMode,
    visualStyle,
    setVisualStyle,
    pythonTutorReferenceArrows,
    setPythonTutorReferenceArrows,
    pythonTutorStandalonePrimitives,
    setPythonTutorStandalonePrimitives,
    showLinkedListView,
    setShowLinkedListView,
    structurePanelCollapsed,
    setStructurePanelCollapsed,
    structurePanelHeight,
    setStructurePanelHeight,
    questionView,
    setQuestionView,
    tabScrollPositions,
    setTabScrollPositions,

    // Panel state
    activePaletteTab,
    setActivePaletteTab,
    isPaletteOpen,
    setIsPaletteOpen,
    isInfoPanelOpen,
    setIsInfoPanelOpen,
    infoPanelWidth,
    setInfoPanelWidth,
    isResizingInfoPanel,
    setIsResizingInfoPanel,

    // Modal state
    showClearCanvasModal,
    setShowClearCanvasModal,
    showModeToggleModal,
    setShowModeToggleModal,
  };
}

export { clearCanvasStorage };
